import {
  Badge,
  Button,
  Callout,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  useToast,
  type BadgeTone,
} from '@provenance/design';
import { UserRole } from '@provenance/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, FileText, Inbox, Mail, MailOpen, Paperclip, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { auDateTime, bytes } from '../lib/format';
import { useSession } from '../lib/session';

type Filter = 'all' | 'unread' | 'requests' | 'waiting' | 'held' | 'duplicate' | 'test' | 'archived';

interface Row {
  id: string;
  fromAddress: string;
  fromName: string | null;
  subject: string;
  outcome: string;
  isTest: boolean;
  read: boolean;
  archived: boolean;
  receivedAt: string;
  attachmentCount: number;
  documentId: string | null;
}

interface Detail {
  id: string;
  fromAddress: string;
  fromName: string | null;
  toAddress: string;
  subject: string;
  textBody: string | null;
  htmlBody: string | null;
  receivedAt: string;
  outcome: string;
  outcomeDetail: string | null;
  isTest: boolean;
  archived: boolean;
  verdicts: { spf?: string; dkim?: string; dkimDomain?: string | null; spam?: string; virus?: string } | null;
  attachments: Array<{ index: number; filename: string; contentType: string; sizeBytes: number; isPdf: boolean; url: string | null }>;
  document: { id: string; senderReference: string; status: string; certificateNo: string | null } | null;
  canCreateRequest: boolean;
}

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'Inbox' },
  { value: 'unread', label: 'Unread' },
  { value: 'requests', label: 'Became requests' },
  { value: 'waiting', label: 'Waiting for sender' },
  { value: 'held', label: 'Needs a look' },
  { value: 'duplicate', label: 'Duplicates' },
  { value: 'test', label: 'Tests' },
  { value: 'archived', label: 'Archived' },
];

export const OUTCOME: Record<string, { label: string; tone: BadgeTone }> = {
  RECEIVED: { label: 'Processing', tone: 'info' },
  CREATED: { label: 'Became a request', tone: 'ok' },
  AWAITING_CONFIRMATION: { label: 'Waiting for sender', tone: 'info' },
  HELD: { label: 'Needs a look', tone: 'warn' },
  DUPLICATE: { label: 'Duplicate', tone: 'neutral' },
  IGNORED: { label: 'Not processed', tone: 'neutral' },
  BLOCKED: { label: 'Blocked', tone: 'stop' },
  FAILED: { label: 'Could not read', tone: 'stop' },
  TEST: { label: 'Test', tone: 'leaf' },
};

/**
 * The chamber's Mailbox (T-136).
 *
 * Everything that reached the chamber's inbound address and what happened to it. Most mail
 * becomes a request on its own; this is where a person deals with the rest — and where
 * "did our email arrive?" gets a definite answer.
 */
export function Mailbox() {
  const { user } = useSession();
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS.some((f) => f.value === params.get('filter')) ? params.get('filter') : 'all') as Filter;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(params.get('open'));

  const query = new URLSearchParams({ filter, page: String(page) });
  if (search.trim()) query.set('q', search.trim());
  const messages = useQuery({
    queryKey: ['mailbox', filter, page, search.trim()],
    queryFn: () =>
      api.get<{ total: number; page: number; pageSize: number; items: Row[] }>(`/api/console/mailbox/messages?${query}`),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  const rows = messages.data?.items ?? [];
  const totalPages = messages.data ? Math.max(1, Math.ceil(messages.data.total / messages.data.pageSize)) : 1;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Mailbox"
        lede="Every email sent or forwarded to your chamber's Provenir address, and what happened to it. Most become requests on their own; the rest wait here for a person."
        actions={
          user?.role === UserRole.CHAMBER_ADMIN ? (
            <Link to="/configuration/mailbox">
              <Button variant="secondary" leadingIcon={<Settings className="size-3.5" />}>
                Mailbox settings
              </Button>
            </Link>
          ) : undefined
        }
      />

      <div role="group" aria-label="Show" className="flex flex-wrap gap-1">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={filter === f.value ? 'primary' : 'ghost'}
            aria-pressed={filter === f.value}
            onClick={() => {
              setParams(f.value === 'all' ? {} : { filter: f.value });
              setPage(1);
            }}
          >
            {f.label}
          </Button>
        ))}
        <Input
          className="ml-auto w-full sm:w-64"
          placeholder="Search sender or subject"
          aria-label="Search sender or subject"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <Card padded={false} className="self-start">
          {messages.isLoading ? (
            <Skeleton className="m-3 h-64" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Inbox className="size-5" />}
              title={search || filter !== 'all' ? 'Nothing here' : 'No email yet'}
              description={
                user?.role === UserRole.CHAMBER_ADMIN
                  ? 'Switch the mailbox on and send yourself a test from Mailbox settings.'
                  : 'Email sent to your chamber’s Provenir address appears here.'
              }
            />
          ) : (
            <>
              <ul className="list-none p-0" aria-label="Messages">
                {rows.map((m) => (
                  <li key={m.id} className="border-b border-[var(--pv-line)] last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setOpenId(m.id)}
                      aria-current={openId === m.id ? 'true' : undefined}
                      className={`flex w-full flex-col gap-0.5 px-4 py-3 text-left hover:bg-[var(--pv-paper-sunk)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--pv-brand)] ${
                        openId === m.id ? 'bg-[var(--pv-paper-sunk)]' : ''
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {!m.read && <span aria-label="Unread" className="size-2 shrink-0 rounded-full bg-[var(--pv-brand)]" />}
                        <span className={`min-w-0 flex-1 truncate ${m.read ? '' : 'font-semibold'}`}>
                          {m.fromName ? `${m.fromName}` : m.fromAddress}
                        </span>
                        <span className="pv-num shrink-0 [font-size:11.5px] text-[var(--pv-faint)]">{auDateTime(m.receivedAt)}</span>
                      </span>
                      <span className={`truncate [font-size:13px] ${m.read ? 'text-[var(--pv-muted)]' : 'font-medium'}`}>
                        {m.subject || '(no subject)'}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge tone={OUTCOME[m.outcome]?.tone ?? 'neutral'} size="sm" dot>
                          {OUTCOME[m.outcome]?.label ?? m.outcome}
                        </Badge>
                        {m.attachmentCount > 0 && (
                          <span className="inline-flex items-center gap-0.5 [font-size:11.5px] text-[var(--pv-faint)]">
                            <Paperclip aria-hidden className="size-3" /> {m.attachmentCount}
                            <span className="sr-only"> attachments</span>
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 border-t border-[var(--pv-line)] p-3 [font-size:12.5px] text-[var(--pv-muted)]">
                <span>
                  {messages.data!.total.toLocaleString('en-AU')} · page {page} of {totalPages}
                </span>
                <Button size="sm" variant="ghost" className="ml-auto" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </>
          )}
        </Card>

        {openId ? (
          <MessagePane id={openId} onClosed={() => setOpenId(null)} />
        ) : (
          <Card className="hidden lg:block">
            <EmptyState icon={<Mail className="size-5" />} title="Choose a message" description="Its content, attachments and what happened to it appear here." />
          </Card>
        )}
      </div>
    </div>
  );
}

function MessagePane({ id, onClosed }: { id: string; onClosed: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [view, setView] = useState<'text' | 'html'>('text');
  const [creating, setCreating] = useState(false);

  const detail = useQuery({
    queryKey: ['mailbox-message', id],
    queryFn: () => api.get<Detail>(`/api/console/mailbox/messages/${id}`),
  });

  // Opening it marked it read on the server; the list and the badge catch up.
  useEffect(() => {
    if (detail.data) {
      void queryClient.invalidateQueries({ queryKey: ['mailbox'] });
      void queryClient.invalidateQueries({ queryKey: ['mailbox-count'] });
    }
  }, [detail.data, queryClient]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['mailbox'] });
    void queryClient.invalidateQueries({ queryKey: ['mailbox-message', id] });
    void queryClient.invalidateQueries({ queryKey: ['mailbox-count'] });
  };
  const fail = (title: string) => (error: unknown) =>
    toast.error(title, error instanceof ApiError ? error.message : 'Something went wrong.');

  const markUnread = useMutation({
    mutationFn: () => api.post(`/api/console/mailbox/messages/${id}/read`, { read: false }),
    onSuccess: () => {
      refresh();
      onClosed();
    },
    onError: fail('Could not mark it unread'),
  });
  const archive = useMutation({
    mutationFn: (archived: boolean) => api.post(`/api/console/mailbox/messages/${id}/archive`, { archived }),
    onSuccess: (_r, archived) => {
      toast.success(archived ? 'Archived' : 'Back in the inbox');
      refresh();
      if (archived) onClosed();
    },
    onError: fail('Could not archive it'),
  });

  const m = detail.data;
  if (detail.isLoading || !m) return <Skeleton className="h-96" />;
  const outcome = OUTCOME[m.outcome] ?? { label: m.outcome, tone: 'neutral' as BadgeTone };

  return (
    <Card className="flex min-w-0 flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 className="[font-size:var(--pv-text-h3)] font-bold">{m.subject || '(no subject)'}</h2>
        <p className="[font-size:13px] text-[var(--pv-muted)]">
          From <strong className="text-[var(--pv-ink)]">{m.fromName ? `${m.fromName} <${m.fromAddress}>` : m.fromAddress}</strong>
          {' · '}
          {auDateTime(m.receivedAt)}
        </p>
        <p className="[font-size:12px] text-[var(--pv-faint)]">To {m.toAddress}</p>
      </header>

      <Callout
        tone={outcome.tone === 'stop' ? 'stop' : outcome.tone === 'warn' ? 'warn' : outcome.tone === 'ok' ? 'ok' : 'info'}
        title={outcome.label}
      >
        {m.outcomeDetail}
        {m.document && (
          <>
            {' '}
            <Link to={`/documents/${m.document.id}`} className="font-medium underline">
              Open request {m.document.certificateNo ?? m.document.senderReference}
            </Link>
          </>
        )}
      </Callout>

      <div className="flex flex-wrap gap-2">
        {m.canCreateRequest && (
          <Button variant="primary" size="sm" leadingIcon={<FileText className="size-3.5" />} onClick={() => setCreating(true)}>
            Create request from this
          </Button>
        )}
        <Button variant="secondary" size="sm" leadingIcon={<MailOpen className="size-3.5" />} loading={markUnread.isPending} onClick={() => markUnread.mutate()}>
          Mark unread
        </Button>
        {m.archived ? (
          <Button variant="secondary" size="sm" leadingIcon={<ArchiveRestore className="size-3.5" />} loading={archive.isPending} onClick={() => archive.mutate(false)}>
            Move to inbox
          </Button>
        ) : (
          <Button variant="secondary" size="sm" leadingIcon={<Archive className="size-3.5" />} loading={archive.isPending} onClick={() => archive.mutate(true)}>
            {m.outcome === 'HELD' ? 'Dismiss and archive' : 'Archive'}
          </Button>
        )}
      </div>

      {m.attachments.length > 0 && (
        <section aria-label="Attachments">
          <p className="pv-eyebrow text-[var(--pv-faint)]">Attachments</p>
          <ul className="mt-1.5 flex list-none flex-col gap-1 p-0">
            {m.attachments.map((a) => (
              <li key={a.index} className="flex items-center gap-2 [font-size:13px]">
                <Paperclip aria-hidden className="size-3.5 text-[var(--pv-faint)]" />
                {a.url ? (
                  <a href={a.url} className="font-medium text-[var(--pv-brand)] hover:underline" target="_blank" rel="noreferrer">
                    {a.filename}
                  </a>
                ) : (
                  <span>{a.filename}</span>
                )}
                <span className="text-[var(--pv-faint)]">{bytes(a.sizeBytes)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="Message" className="flex flex-col gap-2">
        {m.htmlBody && (
          <div role="group" aria-label="View as" className="flex gap-1">
            <Button size="sm" variant={view === 'text' ? 'primary' : 'ghost'} aria-pressed={view === 'text'} onClick={() => setView('text')}>
              Plain text
            </Button>
            <Button size="sm" variant={view === 'html' ? 'primary' : 'ghost'} aria-pressed={view === 'html'} onClick={() => setView('html')}>
              As sent
            </Button>
          </div>
        )}
        {view === 'html' && m.htmlBody ? (
          // No permissions at all: no scripts, no forms, no loading anything as this console.
          <iframe
            title={`Message: ${m.subject}`}
            sandbox=""
            srcDoc={m.htmlBody}
            className="h-[28rem] w-full rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-white"
          />
        ) : (
          <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap break-words rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-3 [font-family:inherit] [font-size:13px]">
            {m.textBody?.trim() || '(no text)'}
          </pre>
        )}
      </section>

      {m.verdicts && (
        <p className="[font-size:12px] text-[var(--pv-faint)]">
          Checks by Amazon SES — sender (SPF): {m.verdicts.spf ?? '—'} · signature (DKIM): {m.verdicts.dkim ?? '—'}
          {m.verdicts.dkimDomain ? ` (${m.verdicts.dkimDomain})` : ''} · spam: {m.verdicts.spam ?? '—'} · virus: {m.verdicts.virus ?? '—'}
        </p>
      )}

      {creating && (
        <CreateRequestDialog
          message={m}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
    </Card>
  );
}

function CreateRequestDialog({ message, onClose, onCreated }: { message: Detail; onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const pdfs = message.attachments.filter((a) => a.isPdf && a.url);
  const [attachmentIndex, setAttachmentIndex] = useState(pdfs[0]?.index ?? 0);
  const [documentTypeId, setDocumentTypeId] = useState('');
  const types = useQuery({
    queryKey: ['mailbox-document-types'],
    queryFn: () => api.get<Array<{ id: string; code: string; name: string }>>('/api/console/mailbox/document-types'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ documentId: string; status: string }>(`/api/console/mailbox/messages/${message.id}/create-request`, {
        attachmentIndex,
        documentTypeId,
      }),
    onSuccess: (r) => {
      toast.success(
        r.status === 'DUPLICATE' ? 'Already a request' : 'Request created',
        r.status === 'DUPLICATE' ? 'This certificate had already arrived; the email is linked to it.' : 'It is now in Pending.',
      );
      onCreated();
    },
    onError: (error) => toast.error('Could not create the request', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title="Create a request from this email"
          description={`You are vouching for this email from ${message.fromAddress}. It goes into Pending like any other request, recorded as accepted from the mailbox by you.`}
        />
        <DialogBody>
          <div className="flex flex-col gap-3.5">
            <Field label="Which attachment is the certificate?" required>
              <NativeSelect value={attachmentIndex} onChange={(e) => setAttachmentIndex(Number(e.target.value))}>
                {pdfs.map((a) => (
                  <option key={a.index} value={a.index}>
                    {a.filename}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Certificate type" required help="Other attachments come along as supporting documents.">
              <NativeSelect value={documentTypeId} onChange={(e) => setDocumentTypeId(e.target.value)}>
                <option value="">Choose…</option>
                {(types.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!documentTypeId} loading={create.isPending} onClick={() => create.mutate()}>
            Create request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
